import type { ASPRefreshUserInfoResponse, LoginInfo } from '@module/apis/client-side/member/type';
import type { IPresetResponse } from '../apis/client-side/preset/type';
import { xDeviceInfoLocalStorage } from '../lib/storage/storageImplementations';
import { queryClient } from '../queries';
import { aspAuthQueryKeys } from '../queries/asp';
import { memberQueryKeys } from '../queries/member/memberQueryKeys';
import { presetQueryKeys } from '../queries/preset/presetQueryKeys';
import parseJsonSafely from '../utils/parseJsonSafely';
import { resolvePresetHostname } from '../utils/site/hostname';
import type { ASPConfig } from './type';

export const sendSyncXHR = (url: URL) => {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', url.toString(), false);

  // cross-origin 요청이므로 쿠키를 보내려면 withCredentials = true 가 필요하고,
  // 서버도 Access-Control-Allow-Credentials: true + Origin echo 로 응답해야 한다.
  // /v1/config/preset 은 현재 백엔드 CORS 가 credentialed 요청을 받지 못해 제외 —
  // 백엔드 준비 후 함께 추가할 것 (FDM-463).
  if (url.pathname.includes('/auth') || url.pathname.includes('/member') || url.pathname.includes('/asp/info')) {
    xhr.withCredentials = true;
  }

  // send x-Device-Info header using xDeviceInfoLocalStorage
  if (url.searchParams.has('aspCode')) {
    const xDeviceInfo = xDeviceInfoLocalStorage.get();
    if (xDeviceInfo) {
      xhr.setRequestHeader('X-Device-Info', JSON.stringify(xDeviceInfo));
    }
  }
  xhr.send();

  try {
    return parseJsonSafely(xhr.responseText);
  } catch (error) {
    console.error('Failed to parse ASP configuration', error);
    return error;
  }
};

export const updateSessionKey = (sessKey: string | undefined, userid?: number) => {
  // update X-Device-Info in localStorage
  const info = xDeviceInfoLocalStorage.get();
  if (info && sessKey) {
    // 기존 X-Device-Info에 세션 키를 추가
    info.ck = { sessKeyAsp: sessKey };
    xDeviceInfoLocalStorage.set(info);
  }
  if (userid) {
    xDeviceInfoLocalStorage.set({
      ...xDeviceInfoLocalStorage.get(),
      ui: userid,
    });
  }
};

export const injectUserInfoToTanstackQuery = (loginInfo: LoginInfo) => {
  if (loginInfo?.userInfo) {
    queryClient.setQueryData(memberQueryKeys.fetchMemberLoginInfo().queryKey, {
      loginInfo,
    });

    queryClient.setQueryData<ASPRefreshUserInfoResponse | undefined>(
      memberQueryKeys.fetchASPRefreshUserInfo().queryKey,
      (oldData) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          loginInfo,
          coinHave: loginInfo.userInfo.coinHave,
        };
      },
    );
  }
};

export const injectASPAuthToTanstackQuery = (authInfo: ASPConfig) => {
  if (authInfo) {
    queryClient.setQueryData(aspAuthQueryKeys.auth.queryKey, authInfo);
  }
};

export const injectPresetToTanstackQuery = (sitePreset: IPresetResponse, hostname: string) => {
  if (sitePreset?.preset) {
    queryClient.setQueryData(presetQueryKeys.fetchPreset(resolvePresetHostname(hostname)).queryKey, sitePreset);
  }
};
